import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Field } from "./field";

export function Select({
  className,
  description,
  disabled = false,
  error,
  items,
  label,
  name,
  onValueChange,
  placeholder,
  required = false,
  triggerClassName,
  value,
}: {
  className?: string;
  description?: ReactNode;
  disabled?: boolean;
  error?: ReactNode;
  items: Array<{ label: string; value: string }>;
  label?: ReactNode;
  name?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  triggerClassName?: string;
  value?: string;
}) {
  return (
    <Field
      className={className}
      description={description}
      error={error}
      label={label}
      required={required}
    >
      <SelectPrimitive.Root
        disabled={disabled}
        name={name}
        onValueChange={onValueChange}
        required={required}
        value={value}
      >
        <SelectPrimitive.Trigger
          className={cn(
            "field-shell flex min-h-11 w-full items-center justify-between gap-3 px-4 text-sm disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            Boolean(error) && "border-destructive ring-2 ring-destructive/10",
            triggerClassName,
          )}
          data-slot="interactive-control"
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon className="flex shrink-0 self-center">
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className="z-50 min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] overflow-hidden rounded-2xl border border-border bg-popover p-1 text-popover-foreground shadow-lg"
            avoidCollisions={false}
            collisionPadding={16}
            data-testid="select-content"
            position="popper"
            side="bottom"
            sideOffset={6}
          >
            <SelectPrimitive.Viewport
              className="max-h-[min(20rem,var(--radix-select-content-available-height))] space-y-1 overflow-y-auto overscroll-contain p-1"
              data-testid="select-viewport"
            >
              {items.map((item) => (
                <SelectPrimitive.Item
                  key={item.value}
                  className="flex min-h-10 cursor-pointer items-center justify-between gap-4 rounded-xl px-3 py-2 text-sm outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[state=checked]:bg-primary/10"
                  value={item.value}
                >
                  <SelectPrimitive.ItemText>
                    {item.label}
                  </SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator>
                    <Check className="h-4 w-4 text-primary" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </Field>
  );
}
